import { useState } from 'react';
import { Box, Button, PasswordInput, Stack, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { z } from 'zod';
import { zodResolver } from '../lib/zod-resolver';
import { resetPassword } from '../lib/auth';
import classes from './LoginPage.module.css';

const schema = z
  .object({
    password: z.string().min(12, 'Мінімум 12 символів'),
    confirm: z.string().min(1, 'Повторіть пароль'),
  })
  .refine((data) => data.password === data.confirm, {
    message: 'Паролі не збігаються',
    path: ['confirm'],
  });

/** Reads `?token=…` from the letter link. Not a route search param: this page is public, and
 * the router-level search-schema machinery lives under the authed tree. */
function tokenFromLocation(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('token');
}

/**
 * Sets a password from the reset/invite letter link.
 *
 * A missing or rejected token collapses to the same terminal state — «посилання недійсне» —
 * because a token that never existed and one the server just rejected are indistinguishable
 * to the person holding the link, and both need the same next action: ask for a new one.
 */
export function SetPasswordPage(): React.JSX.Element {
  const token = tokenFromLocation();
  const [status, setStatus] = useState<'form' | 'success' | 'invalid'>(
    token === null ? 'invalid' : 'form',
  );
  const [busy, setBusy] = useState(false);

  const form = useForm({
    initialValues: { password: '', confirm: '' },
    validate: zodResolver(schema),
  });

  const submit = form.onSubmit(async (values) => {
    if (token === null) {
      setStatus('invalid');
      return;
    }
    setBusy(true);
    try {
      const result = await resetPassword(values.password, token);
      setStatus(result.ok ? 'success' : 'invalid');
    } catch {
      setStatus('invalid');
    } finally {
      setBusy(false);
    }
  });

  return (
    <Box className={classes.root}>
      <Box className={classes.card} data-testid="set-password-card">
        <Stack gap={4} mb="lg">
          <Box className={classes.logo}>S</Box>
          <Text className={classes.title}>SEO Ops Core</Text>
          <Text className={classes.subtitle}>встановлення пароля</Text>
        </Stack>

        {status === 'success' ? (
          <Stack gap="sm">
            <Text data-testid="set-password-success">Пароль встановлено</Text>
            <Button component="a" href="/login" fullWidth data-testid="set-password-login-link">
              Увійти
            </Button>
          </Stack>
        ) : status === 'invalid' ? (
          <Text className={classes.error} data-testid="set-password-invalid" role="alert">
            Посилання недійсне або прострочене — попросіть адміністратора надіслати нове
          </Text>
        ) : (
          <form onSubmit={submit} noValidate>
            <Stack gap="sm">
              <PasswordInput
                label="Новий пароль"
                autoComplete="new-password"
                data-testid="set-password-new"
                {...form.getInputProps('password')}
              />
              <PasswordInput
                label="Повторіть пароль"
                autoComplete="new-password"
                data-testid="set-password-confirm"
                {...form.getInputProps('confirm')}
              />
              <Button type="submit" loading={busy} fullWidth data-testid="set-password-submit">
                Встановити пароль
              </Button>
            </Stack>
          </form>
        )}
      </Box>
    </Box>
  );
}
